/** Normalize display text: title case for names, sentence case for prose. */

import { normalizeIndianMobileDigits } from '../shared/phoneUtils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const URL_RE = /^https?:\/\//i;

/** Only peel leading/trailing whitespace — keep all spaces inside the value. */
function splitTrailingWhitespace(value) {
  const str = String(value ?? '');
  const trailMatch = str.match(/\s+$/);
  const trail = trailMatch ? trailMatch[0] : '';
  const main = trail ? str.slice(0, -trail.length) : str;
  const leadMatch = main.match(/^\s+/);
  const lead = leadMatch ? leadMatch[0] : '';
  const core = lead ? main.slice(lead.length) : main;
  return { lead, core, trail };
}

export function toTitleCase(value) {
  const { lead, core, trail } = splitTrailingWhitespace(value);
  if (!core) return lead + trail;

  const titled = core
    .split(/(\s+)/)
    .map(part => {
      if (/^\s+$/.test(part)) return part;
      const word = part;
      if (!word) return '';
      if (word.length <= 2 && /^[a-z]/i.test(word)) return word.toUpperCase();
      return word
        .split(/(['-])/)
        .map(p => {
          if (p === "'" || p === '-') return p;
          if (!p) return '';
          return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
        })
        .join('');
    })
    .join('');

  return lead + titled + trail;
}

/**
 * Sentence case: capitalize first letter and letters after . ! ? or newlines.
 * While typing, other letters are left as entered (spaces preserved).
 */
export function toSentenceCase(value, { finalize = false } = {}) {
  const { lead, core, trail } = splitTrailingWhitespace(value);
  if (!core) return lead + trail;

  const trimmed = core.trim();
  if (EMAIL_RE.test(trimmed)) return lead + trimmed.toLowerCase() + trail;
  if (URL_RE.test(trimmed)) return lead + core + trail;

  const applyCaps = (input, lowerRest) => {
    let out = '';
    let capNext = true;
    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      if (capNext && /[a-zA-Z]/.test(ch)) {
        out += ch.toUpperCase();
        capNext = false;
      } else {
        out += lowerRest ? ch.toLowerCase() : ch;
      }
      if (/[.!?]/.test(ch)) capNext = true;
      else if (ch === '\n') capNext = true;
    }
    return out;
  };

  if (!finalize) {
    return lead + applyCaps(core, false) + trail;
  }

  const result = core
    .split('\n')
    .map(line => {
      if (!line.trim()) return line;
      return line
        .split(/(?<=[.!?])\s+/)
        .map(chunk => {
          if (!chunk.trim()) return chunk;
          return applyCaps(chunk, true);
        })
        .join(' ');
    })
    .join('\n');

  return lead + result + trail;
}

/**
 * @param {'email'|'phone'|'password'|'url'|'name'|'numeric'|'text'|'multiline'} kind
 * @param {{ final?: boolean }} options — final:true trims and applies full sentence case (use on blur)
 */
export function normalizeFieldText(value, kind = 'text', options = {}) {
  if (value == null) return '';
  let v = String(value);
  if (options.final && kind !== 'multiline') v = v.trim();

  switch (kind) {
    case 'email':
      return v.trim().toLowerCase();
    case 'phone':
      return normalizeIndianMobileDigits(v).slice(0, 10);
    case 'password':
    case 'numeric':
    case 'date':
      return v;
    case 'url':
      return options.final ? v.trim() : v;
    case 'name':
      return options.final ? toTitleCase(v.trim()) : toTitleCase(v);
    case 'multiline': {
      v = v.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      return options.final ? v.replace(/^\s+|\s+$/g, '') : v;
    }
    case 'text':
    default:
      return toSentenceCase(v, { finalize: !!options.final });
  }
}

export function normalizeParsedPayload(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(normalizeParsedPayload);
  const out = { ...obj };
  const titleFields = [
    'name',
    'profileHeading',
    'location',
    'degree',
    'institution',
    'field',
    'designation',
    'currentCompany',
    'city',
    'state',
    'country'
  ];
  const sentenceFields = ['description', 'address', 'availability'];
  for (const key of Object.keys(out)) {
    if (typeof out[key] === 'string') {
      if (titleFields.includes(key)) out[key] = toTitleCase(out[key]);
      else if (sentenceFields.includes(key)) out[key] = toSentenceCase(out[key], { finalize: true });
    } else if (typeof out[key] === 'object') {
      out[key] = normalizeParsedPayload(out[key]);
    }
  }
  return out;
}
